import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  CalendarMonth,
  Person,
  MedicalServices,
  Email,
  Phone
} from '@mui/icons-material';
import { formatPrice } from '../../services/bookingService';

const MobileConfirmation = ({
  booking,
  clinic,
  onReturnHome
}) => {
  return (
    <Box sx={{ textAlign: 'center' }}>
      {/* Success Icon */}
      <Box sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        bgcolor: 'success.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2
      }}>
        <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
      </Box>

      {/* Success Message */}
      <Typography variant="h5" fontWeight="700" gutterBottom>
        Booking Confirmed!
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Your appointment has been successfully booked.
        A confirmation email will be sent shortly.
      </Typography>

      {/* Booking Details Card */}
      <Paper sx={{ p: 3, textAlign: 'left', mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          APPOINTMENT DETAILS
        </Typography>

        {/* Service */}
        {booking.service && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <MedicalServices sx={{ color: 'primary.main', mt: 0.5 }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Service
              </Typography>
              <Typography variant="subtitle1" fontWeight="600">
                {booking.service.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {booking.service.duration_in_minutes} minutes
              </Typography>
            </Box>
          </Box>
        )}

        {/* Practitioner */}
        {booking.practitioner && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Person sx={{ color: 'primary.main', mt: 0.5 }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Practitioner
              </Typography>
              <Typography variant="subtitle1" fontWeight="600">
                {booking.practitioner.name}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Date/Time */}
        {booking.selectedTime && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <CalendarMonth sx={{ color: 'primary.main', mt: 0.5 }} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Date & Time
              </Typography>
              <Typography variant="subtitle1" fontWeight="600">
                {booking.selectedTime.formatted || booking.selectedTime.time_display}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Patient Info */}
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          PATIENT INFORMATION
        </Typography>

        {booking.patient && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Person sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body1">
                {booking.patient.first_name} {booking.patient.last_name}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Email sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                {booking.patient.email}
              </Typography>
            </Box>

            {booking.patient.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Phone sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {booking.patient.phone}
                </Typography>
              </Box>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Payment Info */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="subtitle2" color="text.secondary">
            TOTAL PAID
          </Typography>
          <Typography variant="h6" fontWeight="700" color="primary.main">
            {booking.service?.price === 0 || booking.service?.price === null
              ? 'Free'
              : formatPrice(booking.service?.price || 0)
            }
          </Typography>
        </Box>

        {booking.payment?.transactionId && booking.payment.transactionId !== 'free-appointment' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Transaction ID: {booking.payment.transactionId}
          </Typography>
        )}
      </Paper>

      {/* Clinic Info */}
      {clinic && (
        <Paper sx={{ p: 2, textAlign: 'center', mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="body2" color="text.secondary">
            {clinic.name}
          </Typography>
          {clinic.address && (
            <Typography variant="caption" color="text.secondary">
              {clinic.address}
            </Typography>
          )}
        </Paper>
      )}

      {/* Return Button */}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={onReturnHome}
        sx={{
          height: 56,
          fontSize: '1rem',
          fontWeight: 600
        }}
      >
        Return to Home
      </Button>

      {/* Note */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2 }}
      >
        Please arrive 5-10 minutes before your appointment time.
      </Typography>
    </Box>
  );
};

export default MobileConfirmation;
